import { useEffect, useState } from 'react'
import moment from 'moment'

import {
  formatCurrency,
  formatNumber,
  getDefaultSeason,
  createWorkbook,
  addWorksheet,
  DEFAULTNUMHEADERS,
  saveWorkbook,
  setHeaders,
  setColumnsAndRows,
  numberFormat,
  currencyFormat,
} from '../../utilities/helpers'
import { getFlexPackageSalesSummary, getSeasons } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { SeasonSelector } from '../SeasonSelector'
import { LoadingContainer } from '../LoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

export default function FlexPackageSalesSummaryWrapper({ organization }) {
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')
  const [isLoadingSeasons, setIsLoadingSeasons] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [report, setReport] = useState(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false

    const loadSeasons = async () => {
      if (!organization?.uuid) {
        setLoadError('Missing organization context for report.')
        setIsLoadingSeasons(false)
        return
      }

      setIsLoadingSeasons(true)
      setLoadError('')

      try {
        const seasonsRes = await getSeasons(organization.uuid)
        if (cancelled) return

        const loadedSeasons = seasonsRes?.data || []
        setSeasons(loadedSeasons)

        if (!loadedSeasons.length) {
          setLoadError('No seasons found for this organization.')
          setIsLoadingSeasons(false)
          return
        }

        const defaultSeason = getDefaultSeason(loadedSeasons)
        setSelectedSeason(
          String(defaultSeason?.id || loadedSeasons[0]?.id || '')
        )
      } catch (error) {
        if (cancelled) return
        console.error(
          'Error loading seasons for Flex Package Sales Summary:',
          error
        )
        setLoadError('Unable to load seasons for Flex Package Sales Summary.')
      } finally {
        if (!cancelled) setIsLoadingSeasons(false)
      }
    }

    loadSeasons()

    return () => {
      cancelled = true
    }
  }, [organization?.uuid])

  useEffect(() => {
    let cancelled = false

    const loadReport = async () => {
      if (!organization?.uuid || !selectedSeason) return

      setIsLoadingData(true)
      setLoadError('')

      try {
        const res = await getFlexPackageSalesSummary(
          organization.uuid,
          selectedSeason
        )
        if (cancelled) return
        setReport(res?.data || null)
      } catch (error) {
        if (cancelled) return
        console.error('Error loading Flex Package Sales Summary:', error)
        setLoadError('Unable to load Flex Package Sales Summary.')
        setReport(null)
      } finally {
        if (!cancelled) {
          setIsLoadingData(false)
          completeRefresh()
        }
      }
    }

    loadReport()

    return () => {
      cancelled = true
    }
  }, [organization?.uuid, selectedSeason, refreshToken])

  const handleExcelExport = () => {
    if (!report?.rows?.length) return

    const seasonName = report?.season?.name || 'Season'
    const workbook = createWorkbook()
    const worksheet = addWorksheet('Flex Package Sales', workbook)

    const format = (row) => ({
      package_name: row.flexPackName,
      package_type: row.packageType,
      game_tickets: row.gameTickets,
      list_price: row.listPrice,
      quantity_sold: row.quantitySold,
      gross_sales: row.grossSales,
      net_sales: row.netSales,
    })

    setHeaders('Flex Package Sales Summary', organization?.name, worksheet, [
      `Season: ${seasonName}`,
      `Generated: ${moment().format('MMMM D, YYYY')}`,
    ])

    setColumnsAndRows(
      Object.keys(format(report.rows[0])),
      report.rows,
      format,
      worksheet
    )

    const headerRows = DEFAULTNUMHEADERS + 2
    numberFormat(3, worksheet, headerRows)
    currencyFormat(4, worksheet, headerRows)
    numberFormat(5, worksheet, headerRows)
    currencyFormat(6, worksheet, headerRows)
    currencyFormat(7, worksheet, headerRows)

    saveWorkbook(workbook, 'flex package sales summary')
      .then()
      .catch((err) => alert(err.message))
  }

  if (isLoadingSeasons) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section className="spacer-sm">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Flex Package Sales Summary</h1>
        </div>
        <p className="section-header-desc">
          Quantity sold, package type, and revenue for Flex Packages in the
          selected season.
        </p>
      </header>

      {loadError && !seasons.length ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>{loadError}</p>
          </EmptyContainer>
        </Card>
      ) : seasons.length > 0 ? (
        <>
          <ReportActions
            isLoading={isLoadingData}
            onRefresh={refresh}
            isRefreshing={isLoadingData}
            selected={selectedSeason}
            data={report?.rows}
            downloadReport={handleExcelExport}
          >
            <SeasonSelector
              seasons={seasons}
              selected={selectedSeason}
              setSelected={setSelectedSeason}
              isLoading={isLoadingData}
            />
          </ReportActions>
          <Results
            selectedSeason={selectedSeason}
            isLoading={isLoadingData}
            report={report}
            loadError={loadError}
          />
        </>
      ) : (
        <Card body>
          <EmptyContainer style="center lg">
            <p>No seasons created</p>
            <p className="subtitle">
              Create a season and link Flex Packages to it to view this report.
            </p>
          </EmptyContainer>
        </Card>
      )}
    </section>
  )
}

function Results({ selectedSeason, isLoading, report, loadError }) {
  if (!selectedSeason) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Select a season to view Flex Package sales</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (loadError) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>{loadError}</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (!report?.rows?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No Flex Packages for this season</p>
          <p className="subtitle subtitle--dark">
            There are no Flex Packages linked to the selected season yet.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <Card.Title as="h5" className="mb-3">
        Summary by Flex Package
      </Card.Title>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table className="align-middle">
            <colgroup>
              <col style={{ width: '36%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '11.5%' }} />
              <col style={{ width: '11.5%' }} />
            </colgroup>
            <thead>
              <tr className="md">
                <th className="uppercase text-body">Package name</th>
                <th className="text-left uppercase text-body">Package type</th>
                <th className="text-end uppercase text-body">List price</th>
                <th className="text-center uppercase text-body">Qty sold</th>
                <th className="text-end uppercase text-body">Gross sales</th>
                <th className="text-end uppercase text-body">Net sales</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row) => (
                <tr key={row.flexPackId || row.flexPackUuid} className="md-x">
                  <td>{row.flexPackName}</td>
                  <td className="text-left">{row.packageType}</td>
                  <td className="text-end">{formatCurrency(row.listPrice)}</td>
                  <td className="text-center">
                    {formatNumber(row.quantitySold)}
                  </td>
                  <td className="text-end">{formatCurrency(row.grossSales)}</td>
                  <td className="text-end">{formatCurrency(row.netSales)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="md-x">
                <td colSpan={3}>
                  <strong>Total</strong>
                </td>
                <td className="text-center">
                  <strong>{formatNumber(report.totals?.quantitySold)}</strong>
                </td>
                <td className="text-end">
                  <strong>{formatCurrency(report.totals?.grossSales)}</strong>
                </td>
                <td className="text-end">
                  <strong>{formatCurrency(report.totals?.netSales)}</strong>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>
    </Card>
  )
}
